import type {
  CuerpoTramosUsdt,
  EntradaTramoUsdt,
  HistorialTramoUsdt,
  ModoTramoUsdt,
  TramoUsdtGuardado,
} from '../../services/main-rates.service';
import { formatoMonto, letraDe } from './franjas-editor.rules';

/**
 * Reglas puras de los tramos VES→USDT (editor y bloque del panel). Sin Angular
 * ni HTTP, para probarlas sin navegador.
 *
 * Espejo de `tramos-usdt.calculo.ts` del back: la tabla se mueve al instante y
 * la vista previa oficial llega después con debounce. El back es la autoridad;
 * sus errores y advertencias se muestran siempre.
 *
 * La tasa es VES por USDT: el cliente recibe VES ÷ tasa, así que una tasa más
 * baja es mejor para él. El tramo chico paga la base y los grandes mejoran.
 */

export const MOTIVO_MIN = 5;
export const MOTIVO_MAX = 255;
/** Margen manual máximo (%), igual que el back. */
export const AJUSTE_MAX = 50;
/** Tope del margen mínimo: más que esto es un error de tipeo. */
export const PCT_MAX = 50;
/** Máximo de tramos activos a la vez, igual que el back (los apagados no cuentan). */
export const MAX_ACTIVOS = 30;
/** Máximo de filas que acepta el back en el arreglo (activas y apagadas). */
export const MAX_TRAMOS_TOTAL = 200;
/** Colchón % sobre la tasa de venta USDT→VES contra el ida y vuelta (back: MARGEN_SOBRE_VENTA_PCT). */
export const MARGEN_SOBRE_VENTA_PCT = 0.5;
/** Tolerancia de redondeo para decir que un tramo cobra más que el anterior. */
export const TOLERANCIA_MONOTONIA = 0.005;
export const HISTORIAL_TAMANO = 5;
/** Monto de ejemplo del simulador, en VES. */
export const MONTO_SIM_VES = 100_000;
/** Columna «Paga por 1.000 USDT». */
export const USDT_EJEMPLO = 1_000;
/** Rango del primer tramo cuando no hay ninguno cargado. */
export const PRIMER_TRAMO = { desde: 1, hasta: 1_000_000 };

export const round2 = (x: number): number => Math.round(x * 100) / 100;
export const ceil2 = (x: number): number => Math.ceil(x * 100 - 1e-9) / 100;

// ───────────────────────── Cálculo (espejo del back) ─────────────────────────

/**
 * Tasa base con la comisión sobre el costo de mercado. Solo referencia: la
 * comisión ya no se edita en los tramos (se cambia en su modal de siempre).
 */
export function baseDe(referencia: number | null | undefined, comisionPct: number | null | undefined): number | null {
  const r = Number(referencia);
  const c = Number(comisionPct);
  if (referencia == null || comisionPct == null || !(r > 0) || !Number.isFinite(c)) return null;
  return round2(r * (1 + c / 100));
}

export function margenSobre(tasa: number, referencia: number): number {
  return (tasa / referencia - 1) * 100;
}

/** Margen de la base sobre el costo, en %. */
export function margenBaseDe(referencia: number | null | undefined, base: number | null | undefined): number | null {
  const r = Number(referencia);
  const b = Number(base);
  if (!(r > 0) || !(b > 0)) return null;
  return margenSobre(b, r);
}

/** Cuánto margen se puede regalar entre el tramo chico y el candado. */
export function espacioDe(
  referencia: number | null | undefined,
  base: number | null | undefined,
  margenMinPct: number | null | undefined,
): number | null {
  const m = margenBaseDe(referencia, base);
  const min = Number(margenMinPct);
  if (m == null || margenMinPct == null || !Number.isFinite(min)) return null;
  return Math.max(0, m - min);
}

/**
 * Tasa mínima que ningún tramo puede perforar: el margen mínimo sobre el
 * costo y, si hay tasa de venta USDT→VES, esa venta + 0,5 % (así comprar y
 * vender de vuelta nunca le deja ganancia al cliente). Gana la más alta.
 */
export function pisoDe(
  referencia: number | null | undefined,
  margenMinPct: number | null | undefined,
  tasaVenta?: number | null,
): number | null {
  const r = Number(referencia);
  const min = Number(margenMinPct);
  if (!(r > 0) || margenMinPct == null || !Number.isFinite(min)) return null;
  const porMargen = ceil2(r * (1 + min / 100));
  const v = Number(tasaVenta);
  const porVenta =
    tasaVenta != null && Number.isFinite(v) && v > 0 ? ceil2(v * (1 + MARGEN_SOBRE_VENTA_PCT / 100)) : 0;
  return Math.max(porMargen, porVenta);
}

export interface TasaLocal {
  /** Lo que cobra el tramo: nunca más que el anterior (mínimo acumulado). */
  tasa: number;
  /** La del tramo por sí solo, antes del mínimo acumulado (para validar). */
  tasaPropia: number;
  /** Lo que pidió el modo antes de los candados. */
  calculada: number;
  margenPct: number;
  mejoraPct: number;
  recortado: boolean;
}

/**
 * Tasa de cada tramo activo (ya ordenados por «desde»). Vacío si faltan
 * insumos. Misma cuenta que `calcularTramosUsdt` del back: al final ningún
 * tramo cobra más que el anterior (mínimo acumulado).
 */
export function calcularTasas(
  referencia: number | null | undefined,
  base: number | null | undefined,
  margenMinPct: number | null | undefined,
  tramos: { modo: ModoTramoUsdt; ajuste: number | null }[],
  tasaVenta?: number | null,
): TasaLocal[] {
  const ref = Number(referencia);
  const b = Number(base);
  const min = Number(margenMinPct);
  if (!(ref > 0) || !(b > 0) || margenMinPct == null || !Number.isFinite(min) || min < 0) return [];
  const n = tramos.length;
  if (!n) return [];

  const margenBase = margenSobre(b, ref);
  const espacio = Math.max(0, margenBase - min);
  const piso = pisoDe(ref, min, tasaVenta) as number;

  let anterior = Infinity;
  return tramos.map((t, i) => {
    const pedido =
      t.modo === 'PCT' ? Number(t.ajuste) : n === 1 ? margenBase : margenBase - (espacio * i) / (n - 1);
    const calculada = i === 0 && t.modo === 'AUTO' ? b : round2(ref * (1 + pedido / 100));
    const propia = b <= piso ? b : Math.min(b, Math.max(piso, calculada));
    // Mínimo acumulado: un tramo más grande paga igual o menos que el anterior.
    const tasa = Math.min(propia, anterior);
    anterior = tasa;
    return {
      tasa,
      tasaPropia: propia,
      calculada,
      margenPct: margenSobre(tasa, ref),
      mejoraPct: (1 - tasa / b) * 100,
      // Un centavo de diferencia es redondeo del piso, no un recorte.
      recortado: Math.abs(tasa - calculada) > 0.011,
    };
  });
}

/**
 * Letras de los tramos que, por sí solos, cobrarían más que el anterior. El
 * back bloquea guardar así (`tramosMasCarosQueElAnterior`); acá se avisa antes.
 */
export function tramosMasCaros(locales: TasaLocal[], letras: string[]): string[] {
  return locales
    .map((t, i) => ({ t, i }))
    .filter(({ t, i }) => i > 0 && t.tasaPropia > locales[i - 1].tasaPropia + TOLERANCIA_MONOTONIA)
    .map(({ i }) => letras[i] ?? letraDe(i));
}

/** Errores de tasa que bloquean guardar: a mayor monto, igual o menor tasa. */
export function erroresMonotonia(locales: TasaLocal[], letras: string[]): string[] {
  return tramosMasCaros(locales, letras).map(
    (l) => `El tramo ${l} cobraría más que el anterior: a mayor monto, igual o menor tasa.`,
  );
}

// ───────────────────────── Borrador del editor ─────────────────────────

export interface FilaTramo {
  /** Ausente = tramo nuevo (se manda sin clave). */
  clave?: string;
  letra: string;
  hasta: number | null;
  modo: ModoTramoUsdt;
  /** Margen % en PCT. En AUTO se conserva lo tipeado en la fila, pero viaja 0. */
  ajuste: number | null;
}

/**
 * La comisión NO está acá: se cambia en su modal de siempre porque mueve la
 * tasa base que también usan Vita VES→COP y VES→PEN.
 */
export interface BorradorTramos {
  margenMinPct: number | null;
  /** «Desde» del primer tramo. */
  minimo: number | null;
  /** Activos, en orden de monto. */
  filas: FilaTramo[];
  /** Apagados (los que venían y los quitados acá). Se mandan con activo=false. */
  apagadas: TramoUsdtGuardado[];
}

function reletrar(filas: FilaTramo[]): FilaTramo[] {
  return filas.map((f, i) => ({ ...f, letra: letraDe(i) }));
}

/** Borrador desde GET rates/tramos-usdt/ves-usdt/editor. */
export function borradorDesde(editor: {
  margenMinPct?: number | null;
  tramos?: TramoUsdtGuardado[] | null;
} | null | undefined): BorradorTramos {
  const todos = [...(editor?.tramos || [])].sort((a, b) => a.desde - b.desde || a.orden - b.orden);
  const activos = todos.filter((t) => t.activo);
  return {
    margenMinPct: editor?.margenMinPct ?? null,
    minimo: activos.length ? activos[0].desde : null,
    filas: activos.map((t, i) => ({
      clave: t.clave,
      letra: letraDe(i),
      hasta: t.hasta,
      modo: t.modo === 'PCT' ? 'PCT' : 'AUTO',
      ajuste: Number.isFinite(Number(t.ajuste)) ? Number(t.ajuste) : 0,
    })),
    apagadas: todos.filter((t) => !t.activo).map((t) => ({ ...t })),
  };
}

export function clonar(b: BorradorTramos): BorradorTramos {
  return {
    ...b,
    filas: b.filas.map((f) => ({ ...f })),
    apagadas: b.apagadas.map((t) => ({ ...t })),
  };
}

/** «Desde» encadenado: el primero es el mínimo, el resto el «hasta» anterior + 1. */
export function desdeDe(b: BorradorTramos, i: number): number | null {
  if (i === 0) return b.minimo;
  const prev = b.filas[i - 1]?.hasta;
  return prev == null || !Number.isFinite(Number(prev)) ? null : Number(prev) + 1;
}

export function hastaDe(b: BorradorTramos, i: number): number | null {
  return b.filas[i]?.hasta ?? null;
}

export function esUltima(b: BorradorTramos, i: number): boolean {
  return i === b.filas.length - 1;
}

/** El ajuste solo cuenta en PCT: en AUTO viaja siempre 0. */
function ajusteDe(modo: ModoTramoUsdt, ajuste: any): number {
  return modo === 'PCT' ? Number(ajuste) || 0 : 0;
}

/** SIEMPRE el conjunto completo: activos y apagados con su clave. */
export function entradaDesdeBorrador(b: BorradorTramos): EntradaTramoUsdt[] {
  const activos: EntradaTramoUsdt[] = b.filas.map((f, i) => {
    const e: EntradaTramoUsdt = {
      desde: Number(desdeDe(b, i)),
      hasta: Number(f.hasta),
      modo: f.modo,
      ajuste: ajusteDe(f.modo, f.ajuste),
      activo: true,
    };
    if (f.clave) e.clave = f.clave;
    return e;
  });
  const apagados: EntradaTramoUsdt[] = b.apagadas.map((t) => ({
    clave: t.clave,
    desde: t.desde,
    hasta: t.hasta,
    modo: t.modo,
    ajuste: ajusteDe(t.modo, t.ajuste),
    activo: false,
  }));
  return [...activos, ...apagados];
}

/** Cuerpo de previsualizar (y de guardar, sin el motivo). Sin comisión. */
export function cuerpoDesdeBorrador(b: BorradorTramos): CuerpoTramosUsdt {
  return {
    margenMinPct: Number(b.margenMinPct),
    tramos: entradaDesdeBorrador(b),
  };
}

/** Compara lo que viajaría: un ajuste tipeado en AUTO no cuenta como cambio. */
export function hayCambios(b: BorradorTramos | null, inicial: BorradorTramos | null): boolean {
  if (!b || !inicial) return false;
  return JSON.stringify(cuerpoDesdeBorrador(b)) !== JSON.stringify(cuerpoDesdeBorrador(inicial));
}

// ───────────────────────── Validación ─────────────────────────

function esEntero(v: any): boolean {
  return v != null && v !== '' && Number.isInteger(Number(v));
}

function pctValido(v: any, max: number): boolean {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) && n >= 0 && n <= max;
}

/**
 * Estructura de los tramos activos, igual que `validarEstructuraTramosUsdt`:
 * cortes enteros, el primero desde 1 o más, contiguos, al menos uno y como
 * mucho 30 activos. El ajuste solo se valida en «Manual %».
 */
export function validarEstructura(tramos: EntradaTramoUsdt[]): string[] {
  const errores: string[] = [];
  const activos = tramos.filter((t) => t.activo).sort((a, b) => a.desde - b.desde);
  if (!activos.length) errores.push('Tiene que quedar al menos un tramo activo.');
  if (activos.length > MAX_ACTIVOS) {
    errores.push(`Puede haber hasta ${MAX_ACTIVOS} tramos activos (hay ${activos.length}); apaga alguno.`);
  }
  if (tramos.length > MAX_TRAMOS_TOTAL) {
    errores.push(`Se pueden guardar hasta ${MAX_TRAMOS_TOTAL} tramos entre activos y apagados (hay ${tramos.length}).`);
  }
  activos.forEach((t, i) => {
    const nombre = `Tramo ${letraDe(i)}`;
    if (!Number.isInteger(t.desde) || !Number.isInteger(t.hasta)) {
      errores.push(`${nombre}: los cortes son USDT enteros.`);
      return;
    }
    if (t.desde < 1) errores.push(`${nombre}: «Desde» tiene que ser 1 o más.`);
    if (t.desde > t.hasta) errores.push(`${nombre}: «Desde» es mayor que «Hasta».`);
    if (i > 0 && t.desde !== activos[i - 1].hasta + 1) {
      errores.push(`${nombre}: tiene que empezar en ${activos[i - 1].hasta + 1} (sin huecos ni cruces).`);
    }
    if (t.modo === 'PCT' && !pctValido(t.ajuste, AJUSTE_MAX)) {
      errores.push(`${nombre}: el margen manual va de 0 a ${AJUSTE_MAX} %.`);
    }
  });
  return errores;
}

export function errorMargenMin(b: BorradorTramos): string | null {
  return pctValido(b.margenMinPct, PCT_MAX) ? null : `Número de 0 a ${PCT_MAX}.`;
}

/** Sólo el «Desde» del primero se edita. */
export function errorDesdeFila(b: BorradorTramos, i: number): string | null {
  if (i !== 0) return null;
  return esEntero(b.minimo) && Number(b.minimo) >= 1 ? null : 'Entero de 1 o más, sin decimales.';
}

export function errorHastaFila(b: BorradorTramos, i: number): string | null {
  const f = b.filas[i];
  if (!f) return null;
  if (!esEntero(f.hasta) || Number(f.hasta) < 1) return 'Entero mayor que 0, sin decimales.';
  const d = desdeDe(b, i);
  if (d != null && Number(f.hasta) < d) return 'No puede ser menor que Desde.';
  return null;
}

export function errorAjuste(f: FilaTramo): string | null {
  if (f.modo !== 'PCT') return null;
  return pctValido(f.ajuste, AJUSTE_MAX) ? null : `De 0 a ${AJUSTE_MAX} %.`;
}

/** Problemas que se ven sin preguntarle al servidor. No reemplazan su validación. */
export function erroresBorrador(b: BorradorTramos): string[] {
  const errores: string[] = [];
  if (errorMargenMin(b)) errores.push(`El margen mínimo tiene que ser un número de 0 a ${PCT_MAX} %.`);
  b.filas.forEach((f, i) => {
    const nombre = `Tramo ${f.letra}`;
    const ed = errorDesdeFila(b, i);
    if (ed) errores.push(`${nombre}: «Desde» tiene que ser un entero de 1 o más.`);
    const eh = errorHastaFila(b, i);
    if (eh) errores.push(`${nombre}: «Hasta» ${eh === 'No puede ser menor que Desde.' ? 'no puede ser menor que «Desde».' : 'tiene que ser un entero mayor que 0.'}`);
  });
  if (errores.length) return errores;
  // Con los campos sanos, la misma validación de estructura que el back.
  return validarEstructura(entradaDesdeBorrador(b));
}

export function motivoValido(motivo: string | null | undefined): boolean {
  const n = (motivo || '').trim().length;
  return n >= MOTIVO_MIN && n <= MOTIVO_MAX;
}

// ───────────────────────── Acciones ─────────────────────────

export interface Resultado {
  borrador: BorradorTramos;
  error: string | null;
}

function aNumero(v: any): number | null {
  return v === '' || v == null ? null : Number(v);
}

export function cambiarDesdePrimera(b: BorradorTramos, valor: any): BorradorTramos {
  return { ...b, minimo: aNumero(valor) };
}

export function cambiarHasta(b: BorradorTramos, i: number, valor: any): BorradorTramos {
  return { ...b, filas: b.filas.map((f, idx) => (idx === i ? { ...f, hasta: aNumero(valor) } : f)) };
}

/**
 * Cambia el modo de la fila `i`. Al pasar a «Manual %» arranca con el margen
 * que tenía en automático (`margenActual`), así la tasa no salta.
 */
export function cambiarModo(
  b: BorradorTramos,
  i: number,
  modo: ModoTramoUsdt,
  margenActual: number | null | undefined,
): BorradorTramos {
  return {
    ...b,
    filas: b.filas.map((f, idx) => {
      if (idx !== i || f.modo === modo) return f;
      if (modo === 'PCT') {
        const m = Number(margenActual);
        return { ...f, modo, ajuste: Number.isFinite(m) ? Math.max(0, round2(m)) : 0 };
      }
      return { ...f, modo };
    }),
  };
}

export function cambiarAjuste(b: BorradorTramos, i: number, valor: any): BorradorTramos {
  return { ...b, filas: b.filas.map((f, idx) => (idx === i ? { ...f, ajuste: aNumero(valor) } : f)) };
}

/** ¿Cabe otro tramo activo? Los apagados no cuentan. */
export function puedeAgregar(b: BorradorTramos | null): boolean {
  return !!b && b.filas.length < MAX_ACTIVOS;
}

export function todosAutomaticos(b: BorradorTramos): BorradorTramos {
  return { ...b, filas: b.filas.map((f) => (f.modo === 'AUTO' ? f : { ...f, modo: 'AUTO' as ModoTramoUsdt })) };
}

/**
 * Agrega un tramo AL FINAL: el último se corta a la mitad (redondeado a
 * centenas si cabe) y el nuevo llega hasta donde llegaba el último. Sin
 * tramos, crea el primero.
 */
export function agregarAlFinal(b: BorradorTramos): Resultado {
  if (!puedeAgregar(b)) {
    return { borrador: b, error: `Ya hay ${MAX_ACTIVOS} tramos activos, el máximo: quita uno para agregar otro.` };
  }
  if (!b.filas.length) {
    return {
      borrador: {
        ...b,
        minimo: PRIMER_TRAMO.desde,
        filas: [{ letra: 'A', hasta: PRIMER_TRAMO.hasta, modo: 'AUTO', ajuste: 0 }],
      },
      error: null,
    };
  }
  const i = b.filas.length - 1;
  const d = desdeDe(b, i);
  const h = Number(b.filas[i].hasta);
  if (d == null || !Number.isFinite(h) || !(h > d)) {
    return { borrador: b, error: 'No hay espacio para otro tramo: sube el «Hasta» del último.' };
  }
  const medio = Math.floor((d + h) / 2);
  const redondo = Math.round(medio / 100) * 100;
  const corte = redondo >= d && redondo < h ? redondo : medio;
  const filas = [...b.filas];
  filas[i] = { ...filas[i], hasta: corte };
  filas.push({ letra: '', hasta: h, modo: 'AUTO', ajuste: 0 });
  return { borrador: { ...b, filas: reletrar(filas) }, error: null };
}

/**
 * Apaga el tramo `i`: su rango lo toma el anterior (o el siguiente si es el
 * primero). Si ya existía se manda con activo=false; nunca se borra.
 */
export function quitar(
  b: BorradorTramos,
  i: number,
  originales: TramoUsdtGuardado[] | null | undefined,
): Resultado {
  const f = b.filas[i];
  if (!f) return { borrador: b, error: null };
  if (b.filas.length <= 1) return { borrador: b, error: 'Tiene que quedar al menos un tramo.' };
  const filas = [...b.filas];
  if (i > 0) filas[i - 1] = { ...filas[i - 1], hasta: f.hasta };
  filas.splice(i, 1);
  const apagadas = [...b.apagadas];
  if (f.clave) {
    const orig = (originales || []).find((o) => o.clave === f.clave);
    if (orig) apagadas.push({ ...orig, activo: false });
  }
  return { borrador: { ...b, filas: reletrar(filas), apagadas }, error: null };
}

// ───────────────────────── Barra ─────────────────────────

export interface SegmentoTramo {
  letra: string;
  desde: number;
  hasta: number;
  ancho: number;
}

/** Un segmento por tramo, en escala logarítmica (los chicos se verían nada). */
export function segmentosBarra(b: BorradorTramos): SegmentoTramo[] {
  const tramos: { letra: string; desde: number; hasta: number }[] = [];
  for (let i = 0; i < b.filas.length; i++) {
    const d = Number(desdeDe(b, i));
    const h = Number(hastaDe(b, i));
    if (!(d >= 1) || !(h >= d) || !Number.isFinite(d) || !Number.isFinite(h)) return [];
    tramos.push({ letra: b.filas[i].letra, desde: d, hasta: h });
  }
  const pesos = tramos.map((t) => Math.log10(t.hasta + 1) - Math.log10(t.desde));
  const total = pesos.reduce((s, p) => s + p, 0);
  if (!(total > 0)) return [];
  return tramos.map((t, i) => ({ ...t, ancho: (pesos[i] / total) * 100 }));
}

// ───────────────────────── Simulador ─────────────────────────

export interface SimulacionUsdt {
  /** Tramo que aplica, o -1. */
  indice: number;
  /** USDT aproximados a la tasa base, con los que se ubica el tramo. */
  usdtBase: number | null;
  mensaje: string | null;
}

/**
 * Qué tramo aplica a una compra de `montoVes`. El tramo se ubica con los
 * USDT a la tasa base; el último tramo cubre también lo que pase su «Hasta».
 */
export function simular(
  tramos: { desde: number | null }[],
  base: number | null | undefined,
  montoVes: number | null | undefined,
): SimulacionUsdt {
  const m = Number(montoVes);
  const b = Number(base);
  if (montoVes == null || !(m > 0)) return { indice: -1, usdtBase: null, mensaje: 'Escribe un monto mayor que 0.' };
  if (!(b > 0)) return { indice: -1, usdtBase: null, mensaje: 'No hay tasa base para ubicar el tramo.' };
  if (!tramos.length) return { indice: -1, usdtBase: m / b, mensaje: 'Sin tramos: todos pagan la tasa base.' };
  const usdt = m / b;
  let indice = 0;
  tramos.forEach((t, i) => {
    if (t.desde != null && usdt >= Number(t.desde)) indice = i;
  });
  return { indice, usdtBase: usdt, mensaje: null };
}

/** USDT que recibe el cliente por `montoVes` con la tasa `tasa`. */
export function recibeUsdt(montoVes: number | null | undefined, tasa: number | null | undefined): number | null {
  const m = Number(montoVes);
  const t = Number(tasa);
  if (!(m > 0) || !(t > 0)) return null;
  return m / t;
}

// ───────────────────────── Textos ─────────────────────────

function n0(v: number | null | undefined): string {
  return v == null || !Number.isFinite(Number(v)) ? '—' : Math.round(Number(v)).toLocaleString('es-CL');
}

function f2(v: number): string {
  return Number(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function p2(v: number | null | undefined): string {
  return v == null || !Number.isFinite(Number(v))
    ? '—'
    : `${Number(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
}

/** «100 – 999 USDT»; con `abierto` el último es «1.000 o más USDT». */
export function etiquetaRangoUsdt(
  desde: number | null | undefined,
  hasta: number | null | undefined,
  abierto = false,
): string {
  if (abierto) return `${n0(desde)} o más USDT`;
  return `${n0(desde)} – ${n0(hasta)} USDT`;
}

function nombreModo(m: ModoTramoUsdt, ajuste: number | null): string {
  return m === 'PCT' ? `manual ${p2(ajuste)}` : 'automático';
}

/** Lista corta de lo que cambia frente a lo cargado. Vacía = sin cambios. */
export function resumenCambios(b: BorradorTramos | null, inicial: BorradorTramos | null): string[] {
  if (!b || !inicial) return [];
  const out: string[] = [];
  if (Number(b.margenMinPct) !== Number(inicial.margenMinPct)) {
    out.push(`Margen mínimo: ${p2(inicial.margenMinPct)} → ${p2(b.margenMinPct)}`);
  }

  const antes = new Map<string, { desde: number | null; hasta: number | null; modo: ModoTramoUsdt; ajuste: number | null }>();
  inicial.filas.forEach((f, i) => {
    if (f.clave) antes.set(f.clave, { desde: desdeDe(inicial, i), hasta: f.hasta, modo: f.modo, ajuste: f.ajuste });
  });

  const vistos = new Set<string>();
  b.filas.forEach((f, i) => {
    const d = desdeDe(b, i);
    if (!f.clave) {
      out.push(`Tramo nuevo ${f.letra}: ${n0(d)}–${n0(f.hasta)} USDT, ${nombreModo(f.modo, f.ajuste)}`);
      return;
    }
    vistos.add(f.clave);
    const a = antes.get(f.clave);
    if (!a) return;
    if (a.desde !== d || Number(a.hasta) !== Number(f.hasta)) {
      out.push(`Tramo ${f.letra}: ${n0(a.desde)}–${n0(a.hasta)} → ${n0(d)}–${n0(f.hasta)} USDT`);
    }
    if (a.modo !== f.modo || (f.modo === 'PCT' && Number(a.ajuste) !== Number(f.ajuste))) {
      out.push(`Tramo ${f.letra}: ${nombreModo(a.modo, a.ajuste)} → ${nombreModo(f.modo, f.ajuste)}`);
    }
  });
  inicial.filas.forEach((f, i) => {
    if (f.clave && !vistos.has(f.clave)) {
      out.push(`Se apaga el tramo ${f.letra} (${n0(desdeDe(inicial, i))}–${n0(f.hasta)} USDT)`);
    }
  });
  return out;
}

/**
 * Aviso de tasa fija manual (MANUAL_FIJO) con la base `x` y el piso. Vacío
 * si falta la base.
 */
export function avisoManualFijo(base: number | null | undefined, piso: number | null | undefined): string {
  const b = Number(base);
  if (base == null || !(b > 0)) return '';
  const x = f2(b);
  const p = Number(piso);
  const hayPiso = piso != null && Number.isFinite(p) && p > 0;
  if (hayPiso && b <= p) {
    return `La tasa fija ${x} está en el piso (${f2(p)}) o debajo: todos los clientes pagan ${x}, sin descuento por tramo.`;
  }
  return (
    `Tasa fija activa (se revierte sola en 9 a 12 minutos): el tramo chico pasa a ${x} VES/USDT para todos los clientes. ` +
    `Los tramos automáticos se reparten entre ${x} y ${hayPiso ? f2(p) : '—'}; los manuales no superan ${x}.`
  );
}

/**
 * Filas del historial que dicen algo: una edición que solo movió el orden
 * (resumen vacío) se esconde, salvo comisión y margen mínimo.
 */
export function historialVisible(items: HistorialTramoUsdt[] | null | undefined): HistorialTramoUsdt[] {
  return (items || []).filter(
    (h) =>
      h.accion !== 'EDITAR' ||
      h.clave === '_comision' ||
      h.clave === '_margen_min' ||
      h.clave === '_tope' ||
      !!resumenHistorial(h),
  );
}

/** Nombre legible de la clave del historial. */
export function etiquetaClave(clave: string | null | undefined): string {
  if (clave === '_comision') return 'Comisión';
  if (clave === '_margen_min') return 'Margen mínimo';
  // Solo CLP↔USDT (22/10/2026): VES nunca lo graba.
  if (clave === '_tope') return 'Tope';
  return clave || '—';
}

function foto(v: any): any {
  if (typeof v === 'string') {
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  }
  return v;
}

function valorHist(k: string, v: any): string {
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (v == null || v === '') return '—';
  if (k === 'ajuste' || k === 'pct') return p2(v);
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString('es-CL') : String(v);
  return String(v);
}

const CAMPOS_HIST = ['desde', 'hasta', 'modo', 'ajuste', 'activo'];

/** Valor suelto de una foto de comisión o margen: número o `{ valor }`. */
function escalar(v: any): any {
  if (v == null || typeof v !== 'object') return v;
  for (const k of ['comisionPct', 'margenMinPct', 'topePct', 'valor', 'pct']) {
    if (k in v) return v[k];
  }
  const vals = Object.values(v);
  return vals.length === 1 ? vals[0] : undefined;
}

/**
 * «hasta: 999 → 1.499 · modo: AUTO → PCT». Comisión y margen mínimo vienen
 * como número suelto (o un objeto con un solo valor): «3,00 % → 2,50 %».
 */
export function resumenHistorial(h: HistorialTramoUsdt | null | undefined): string {
  if (!h) return '';
  const antes = foto(h.antes);
  const despues = foto(h.despues);
  const esPct = h.clave === '_comision' || h.clave === '_margen_min' || h.clave === '_tope';

  if (esPct || (despues != null && typeof despues !== 'object')) {
    const fmt = (v: any) => (esPct ? p2(escalar(v)) : valorHist('', escalar(v)));
    if (despues == null) return '';
    return antes == null ? fmt(despues) : `${fmt(antes)} → ${fmt(despues)}`;
  }

  if (despues == null || typeof despues !== 'object') return '';
  if (antes == null || typeof antes !== 'object') {
    return CAMPOS_HIST.filter((k) => k in despues).map((k) => `${k}: ${valorHist(k, despues[k])}`).join(' · ');
  }
  return Object.keys({ ...antes, ...despues })
    .filter((k) => !['id', 'clave', 'orden', 'updatedAt', 'createdAt'].includes(k))
    .filter((k) => String(antes[k]) !== String(despues[k]))
    .map((k) => `${k}: ${valorHist(k, antes[k])} → ${valorHist(k, despues[k])}`)
    .join(' · ');
}

export { formatoMonto, letraDe };

// ───────────────────────── Presentación (22/10) ─────────────────────────

/** Con más tramos que esto la barra pasa a fichas (también en el celular). */
export const MAX_TRAMOS_BARRA = 8;

/**
 * Banner de tasa manual vigente: «Tasa manual vigente hasta las HH:MM. Todos
 * los tramos cobran X hasta esa hora.» Null si no hay manual o no se puede
 * leer la hora o la tasa.
 */
export function textoManualVigente(
  manual: { tasa: number; hasta: string } | null | undefined,
  esVenta = false,
): string | null {
  if (!manual) return null;
  const tasa = Number(manual.tasa);
  const d = new Date(manual.hasta);
  if (!(tasa > 0) || isNaN(d.getTime())) return null;
  const hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const x = tasa.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // VES→USDT: el cliente compra y le COBRAMOS; USDT→VES: le PAGAMOS los VES.
  return `Tasa manual vigente hasta las ${hora}. Todos los tramos ${esVenta ? 'pagan' : 'cobran'} ${x} hasta esa hora.`;
}
